/**
 * DropPageMain — resolves STYLE for a successfully loaded Drop, then gets out
 * of the way.
 *
 * Originally extracted from DropPage.tsx to keep that file under the 400-line
 * ceiling; this file then grew its own 1,184-line version of the same
 * problem by holding both structures' JSX inline. Now it is only the
 * dispatch: `resolveStyle` answers for the channel this surface is being
 * served on, and each style delegates wholesale to its own component —
 * `DropHubSurface` for HUB, `DropPageSurface` for PAGE. Neither owns data the
 * other needs (a roster and a news feed are HUB-only), so splitting them
 * stopped every page-style Drop paying for hub fetches, and vice versa.
 */

import React, {useState} from "react";
import type {TenantId} from "@/widgets/ThemeProvider";

import {resolveStyle, channelForHost, SURFACE_STYLE} from "@/surfaces/resolveStyle";
import LoginModal from "@/components/modals/LoginModal";
import {ChallengeLoginModal} from "@/components/modals/ChallengeLoginModal";
import {InviteModal, InviteModalType} from "@/components/InviteModal";
import AtlasChatWidget from "@/components/AtlasChatWidget";
import {useCreateDropOptIn} from "@/hooks/drop/useCreateDropOptIn";
import {DropTermsAcceptModal} from "@/pages/drop/DropTermsAcceptModal";
import {useGetDropMemberStatus} from "@/hooks/drop/useGetDropMemberStatus";
import {DropHubSurface} from "./DropHubSurface";
import {DropPageSurface} from "./DropPageSurface";

import type {DropSection} from "@/hooks/drop/useGetDrop";

/*
 * Loose types — DropPage already does the casting; this prop bag receives the
 * already-cast / already-resolved values, so we lean on `any`/`unknown` here
 * rather than re-deriving the prop trees of every downstream widget.
 */
export interface DropPageMainProps {
    /**
     * The full drop response. Typed as `any` here because DropPage has
     * already done the runtime narrowing (known non-null, casted to the
     * widget tree's expected shapes). Re-deriving the prop tree of every
     * downstream widget here would be more typing without more safety.
     */
    drop: any;
    /** URL slug for the drop — used by Hero + DropInSubRail. */
    slug?: string;
    /** Active platform tenant (DevourPlay native or Kick host). */
    tenantId?: TenantId;

    /** True for PUBLIC drops (rendered inside the DevourPlay app shell). */
    isPublicHub: boolean;
    /** Redux selector — whether the login modal should be visible. */
    isLoginOpen: boolean;
    /** Redux dispatch — used to toggle the login modal for PRIVATE drops. */
    dispatch: (action: any) => any;

    /** All sections enabled on this drop (powers the Nav widget). */
    enabledSections: DropSection[];
    /** Convenience reference to the STREAMING section (header quick-access). */
    streamingSection?: DropSection;
    /** Convenience reference to the INFO section (header quick-access). */
    infoSection?: DropSection;
    /** Currently-active section id; null when on home view. */
    activeSection: string | null;
    /** Setter for activeSection (Nav clicks, scroll restoration). */
    setActiveSection: (id: string | null) => void;
    /** Resolved DropSection object for activeSection (passed to renderSectionContent). */
    currentSection?: DropSection | null;
    /** Closure built by DropPage that knows how to render any section type. */
    renderSectionContent: (section: DropSection) => React.ReactNode;

    /** True when at least one featured stream is live (drives Hero compact mode). */
    hasLiveStreams: boolean;
    /** Hub-pulse counters (active members, in-game, streams). Stubbed to undefined for Drops. */
    pulse: any;
    /** Whether the pulse query is loading. Stubbed to false for Drops. */
    pulseLoading: boolean;
    /** Discord widget online member count (powers Hero's "● N online" stat). */
    discordOnlineCount?: number;

    /** Authenticated user profile — passes through to OwnerSocialDiscoveryCTA + ContentBlocks. */
    /*
     * The signed-in member. Narrowed here to what this file reads — the object
     * is the full `useGetUserProfile` user, so the name fields were always
     * present and simply not declared, which is why the hub's navbar had no
     * member block to render.
     */
    user?: {
        id?: string;
        experiencePoints?: number;
        level?: number;
        nickname?: string;
        firstName?: string;
        lastName?: string;
        gravatar?: string;
        /** Uploaded avatar. The hub's account menu draws this before the monogram. */
        profilePicture?: { cdnUrl?: string };
    };
    /** Triggers the login modal for non-auth actions. */
    handleLoginRequired: () => void;
    /** Triggers the invite modal flow (header gift icon). */
    handleInviteClick: () => void;
    /** ?dropRef= attribution from the URL. */
    referralUserId?: string;
    /** Final resolved banner URL (Hero falls back through several cascade options). */
    resolvedHeroImageUrl?: string;
    /** Default DevourPlay XP display config (custom points not supported on Drops). */
    pointDisplay: {
        pointsName: string;
        pointsIconUrl: string | undefined;
        currentPoints: number;
        currentLevel: number;
        nextLevelPoints: number;
    };

    /** Content blocks filtered to the active page (single-page → all blocks). */
    activePageBlocks: any[];
    /** True when the drop has been authored with multiple pages. */
    isMultiPage: boolean;
    /** Page tab labels (only populated when isMultiPage). */
    pageLabels: string[];
    /** Index of the currently-active page within pageLabels. */
    activePageIndex: number;
    /** Setter for activePageIndex (page tab clicks). */
    setActivePageIndex: (i: number) => void;

    /** Whether the invite modal is currently visible. */
    isInviteModalOpen: boolean;
    /** Setter for the invite modal visibility. */
    setIsInviteModalOpen: (open: boolean) => void;

    /** Active challenge data (drives the Explore More promotions strip). */
    missionsChallenge?: any;

    /**
     * Redux action creator passed in rather than imported here to keep
     * this file pure-ish (no side-effect imports beyond ThemeProvider +
     * the widgets it composes).
     */
    toggleLoginOpen: (open: boolean) => any;
}

export const DropPageMain: React.FC<DropPageMainProps> = ({
    drop, tenantId, isPublicHub, isLoginOpen, dispatch,
    enabledSections, streamingSection, infoSection, activeSection, setActiveSection, currentSection, renderSectionContent,
    hasLiveStreams, pulse, pulseLoading, discordOnlineCount,
    user, handleLoginRequired, handleInviteClick, referralUserId, resolvedHeroImageUrl, pointDisplay,
    activePageBlocks, isMultiPage, pageLabels, activePageIndex, setActivePageIndex,
    isInviteModalOpen, setIsInviteModalOpen, missionsChallenge, toggleLoginOpen,
}) => {
    /*
     * STRUCTURE — which layout this surface renders in, for the channel it is
     * being served on.
     *
     * Style belongs to the channel, not the surface: the same Drop is PAGE in
     * the Drop-In directory and can be HUB on its own domain. `resolveStyle`
     * answers for one channel; it is NOT the chrome question, which
     * `DropPageRouter` answers separately from the same input.
     *
     * Every Drop in production has no `distributions[]`, so every one resolves
     * to PAGE and falls through to the existing tree below unchanged. Nothing
     * renders differently until a surface is deliberately given a distribution.
     */
    const channel = channelForHost(
        typeof window === "undefined" ? "" : window.location.hostname,
        ["devourplay.gg", "localhost"],
        isPublicHub,
    );
    const style = resolveStyle(drop.distributions, channel);

    /*
     * HUB returns its OWN tree rather than rendering above the page one.
     *
     * The two structures are alternatives, not layers: the hub positions the
     * same sections the page would have stacked, using the same
     * `renderSectionContent` handed in untouched. Structure decides where a box
     * goes and never looks inside it — which is why turning a Drop from PAGE to
     * HUB is a distribution row rather than a rebuild.
     */
    // SURFACE_STYLE, not the client enum — see the interop note in resolveStyle.ts
    if (style === SURFACE_STYLE.HUB) {
        return (
            <>
                <DropHubSurface
                    drop={drop}
                    style={style}
                    tenantId={tenantId}
                    isPublicHub={isPublicHub}
                    enabledSections={enabledSections}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    renderSectionContent={renderSectionContent}
                    user={user}
                    pointDisplay={pointDisplay}
                    handleInviteClick={handleInviteClick}
                    handleLoginRequired={handleLoginRequired}
                    dispatch={dispatch}
                />
                {/*
                  * The login door, same switch as the PAGE tree. The hub's
                  * sign-in dispatches `toggleLoginOpen` — without a listener
                  * here the click was a silent no-op on hub-style Drops.
                  */}
                {!isPublicHub &&
                    <HubDoor
                        drop={drop}
                        isLoginOpen={isLoginOpen}
                        dispatch={dispatch}
                        toggleLoginOpen={toggleLoginOpen}
                    />}
                {/*
                  * Same missing-listener class as the login modal: the account
                  * menu's Invite dispatches into state only the PAGE tree
                  * rendered, and Help chat toggles a widget no drop tree
                  * mounted at all.
                  */}
                <InviteModal
                    isVisible={isInviteModalOpen}
                    type={InviteModalType.HUB}
                    onClose={() => setIsInviteModalOpen(false)}
                    hubData={{
                        slug: drop.slug,
                        name: drop.name,
                        backgroundUrl: resolvedHeroImageUrl,
                    }}
                    alwaysUseDarkBackground={true}
                    brandColorOverride={drop.themeColors?.primary}
                />
                <AtlasChatWidget allowAnonymous />
            </>
        );
    }

    return (
        <DropPageSurface
            drop={drop}
            tenantId={tenantId}
            isPublicHub={isPublicHub}
            isLoginOpen={isLoginOpen}
            dispatch={dispatch}
            enabledSections={enabledSections}
            streamingSection={streamingSection}
            infoSection={infoSection}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            currentSection={currentSection}
            renderSectionContent={renderSectionContent}
            hasLiveStreams={hasLiveStreams}
            pulse={pulse}
            pulseLoading={pulseLoading}
            discordOnlineCount={discordOnlineCount}
            user={user}
            handleLoginRequired={handleLoginRequired}
            handleInviteClick={handleInviteClick}
            referralUserId={referralUserId}
            resolvedHeroImageUrl={resolvedHeroImageUrl}
            pointDisplay={pointDisplay}
            activePageBlocks={activePageBlocks}
            isMultiPage={isMultiPage}
            pageLabels={pageLabels}
            activePageIndex={activePageIndex}
            setActivePageIndex={setActivePageIndex}
            isInviteModalOpen={isInviteModalOpen}
            setIsInviteModalOpen={setIsInviteModalOpen}
            missionsChallenge={missionsChallenge}
            toggleLoginOpen={toggleLoginOpen}
        />
    );
};

/**
 * The HUB branch's login door. Its own component so the door hooks (opt-in
 * mutation + member-status query) mount only where they're consumed — at the
 * top of DropPageMain they also ran on every PAGE-style render, a wasted
 * query subscription on the default path.
 */
const HubDoor: React.FC<{
    drop: any;
    isLoginOpen: boolean;
    dispatch: (action: any) => any;
    toggleLoginOpen: (open: boolean) => any;
}> = ({drop, isLoginOpen, dispatch, toggleLoginOpen}) => {
    const dropOptIn = useCreateDropOptIn();
    /* Embed visitors only (handshake-gated): Welcome-Back vs Join + field hiding. */
    const dropMemberStatus = useGetDropMemberStatus(drop?.slug, Boolean(drop?.onboardingExperienceId));
    /*
     * A member whose attestation is missing is one carried over from a prior
     * campaign on this door: signed in, so the login modal never opens, and
     * never asked. Dismissible — declining is not a ban, it just leaves them
     * off the board until they accept.
     */
    const [termsDismissed, setTermsDismissed] = useState(false);
    const status = dropMemberStatus.data;
    const needsTerms = Boolean(status?.hasMembership) && status?.attestationOnFile === false;

    if (!drop.onboardingExperienceId) {
        return (
            <LoginModal
                isOpen={isLoginOpen}
                onClose={() => dispatch(toggleLoginOpen(false))}
            />
        );
    }

    return (
        <>
            <ChallengeLoginModal
                isOpen={isLoginOpen}
                onClose={() => dispatch(toggleLoginOpen(false))}
                dropSlug={drop.slug}
                /*
                 * The codeless door opts in server-side; the email door must opt
                 * in here or the member never reaches the membership registry.
                 * Fire-and-forget: idempotent, and a failed opt-in must not read
                 * as a failed login.
                 */
                onLoginSuccess={data => {
                    dropOptIn.mutate({
                        slug: drop.slug,
                        declaredCountry: data?.declaredCountry,
                        declaredState: data?.declaredState,
                        termsVersion: status?.requiredTermsVersion,
                    }, {
                    /*
                     * A failed opt-in leaves a signed-in member with no
                     * membership — earning nothing, off every board — and
                     * silence made that indistinguishable from success.
                     * Re-read the status so the terms prompt can ask.
                     */
                        onError: () => dropMemberStatus.refetch(),
                    });
                    dispatch(toggleLoginOpen(false));
                }}
                /* Location capture — captured, never gating. */
                geoFirst
                /*
                 * Everyone is eligible here. This surface resets weekly and nobody
                 * advances off it to a US Finals seat, so the Race's US-only prize
                 * framing would be false — declared rather than inherited, because
                 * geoFirst forces admitAllCountries true and that used to be read
                 * as a prize rule.
                 */
                prizeEligibility="OPEN"
                memberStatus={dropMemberStatus.data}
                title={dropMemberStatus.data?.hasMembership
                    ? `Welcome back to ${drop.name}`
                    : `Welcome to ${drop.name}`}
                description="Sign in to start earning points, join community activities and climb the board."
                /*
                 * The modal's brand face keys off `challenge`; an onboarding Drop
                 * has none, so the Drop supplies the knobs. EMAIL-only: a social
                 * provider here would mint an account outside the experience.
                 */
                challenge={{
                    brandColor: drop.themeColors?.primary,
                    allowedSignupMethods: ["EMAIL"],
                } as any}
            />
            <DropTermsAcceptModal
                isOpen={needsTerms && !termsDismissed}
                dropName={drop.name}
                officialRulesUrl={status?.officialRulesUrl}
                isSubmitting={dropOptIn.isPending}
                onDismiss={() => setTermsDismissed(true)}
                onAccept={() => {
                    dropOptIn.mutate({
                        slug: drop.slug,
                        termsVersion: status?.requiredTermsVersion,
                    }, {
                    /*
                     * Refetch rather than assume — the server decides whether
                     * the attestation counted.
                     */
                        onSuccess: () => dropMemberStatus.refetch(),
                    });
                }}
            />
        </>
    );
};
